import React, { useState } from 'react';
import { ShieldCheck, UserRound } from 'lucide-react';
import { ANSWERS, QUESTIONS, scoreQuickCheck } from './quickCheck';

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content || '';

// The page behind an advisor's or an enterprise's Succeedo link (Phase 5,
// PRD §5-1, §5-2). Who sent the owner, a short readiness check with a stage
// and weak spots (no dollar figures, nothing saved), and the way in.
export default function SponsorLanding({ kind, enterprise, advisor, signedIn, connectUrl, signupUrl, notice = null }) {
  const [answers, setAnswers] = useState({});
  const [contactOptIn, setContactOptIn] = useState(false);
  const result = scoreQuickCheck(answers);
  const fromAdvisor = Boolean(advisor);

  return (
    <main className="min-h-screen bg-background px-4 py-10" data-testid="sponsor-landing">
      <div className="mx-auto max-w-2xl flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
            Succeedo · offered by {enterprise}
          </span>
          <h1 className="m-0 text-[28px] font-medium text-foreground">How ready is your business for what's next?</h1>
          {fromAdvisor ? (
            <p className="m-0 flex items-center gap-2 text-[15px] text-foreground" data-testid="landing-advisor">
              <UserRound size={18} className="text-muted-foreground" />
              {advisor.name}
              {advisor.title ? `, ${advisor.title}` : ''} invited you. They'll be on your team in Succeedo.
            </p>
          ) : (
            <p className="m-0 text-[15px] text-muted-foreground">
              Free for business owners. Take the two-minute check, then see your value range and plan with a free account.
            </p>
          )}
        </header>

        <section className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-4" aria-label="Quick readiness check">
          <h2 className="m-0 text-[17px] font-medium text-foreground">Two-minute readiness check</h2>
          {QUESTIONS.map((q) => (
            <fieldset key={q.key} className="m-0 p-0 border-0 flex flex-col gap-2">
              <legend className="text-[14px] text-foreground mb-1">{q.text}</legend>
              <div className="flex gap-2">
                {ANSWERS.map((a) => (
                  <button
                    key={a.value}
                    type="button"
                    data-testid={`qc-${q.key}-${a.value}`}
                    aria-pressed={answers[q.key] === a.value}
                    onClick={() => setAnswers({ ...answers, [q.key]: a.value })}
                    className={`rounded-full px-4 py-1.5 text-[13px] border ${
                      answers[q.key] === a.value ? 'bg-foreground text-background border-foreground' : 'border-border text-foreground'
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </fieldset>
          ))}
        </section>

        {result && (
          <section className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-2" data-testid="quick-check-result">
            <span className="font-mono text-[11px] uppercase text-muted-foreground">Your stage</span>
            <h2 className="m-0 text-[20px] font-medium text-foreground">{result.stage.label}</h2>
            <p className="m-0 text-[14px] text-muted-foreground">{result.stage.text}</p>
            {result.weakSpots.length > 0 && (
              <p className="m-0 text-[14px] text-foreground">
                Where to look first: {result.weakSpots.join(', ')}.
              </p>
            )}
            <p className="m-0 text-[12px] text-muted-foreground">
              A quick indication, not advice. Nothing you answered here is saved.
            </p>
          </section>
        )}

        <section className="flex flex-col gap-3">
          {signedIn && notice && (
            <p className="m-0 rounded-xl border border-border bg-muted/40 p-4 text-[14px] text-foreground" role="status" data-testid="landing-notice">
              {notice}
            </p>
          )}
          {signedIn && !notice && (
            <form method="post" action={connectUrl} className="flex flex-col gap-3">
              <input type="hidden" name="authenticity_token" value={csrfToken()} />
              {!fromAdvisor && (
                <label className="flex items-start gap-2 text-[14px] text-foreground">
                  <input type="checkbox" name="contact_opt_in" value="1" checked={contactOptIn} onChange={() => setContactOptIn(!contactOptIn)} />
                  Have a {enterprise} advisor contact me
                </label>
              )}
              <button type="submit" className="self-start rounded-full bg-[hsl(var(--succeedo-orange))] text-white px-6 py-3 text-[15px] font-medium" data-testid="landing-connect">
                {fromAdvisor ? `Add ${advisor.name} to my team` : `Connect my Succeedo to ${enterprise}`}
              </button>
            </form>
          )}
          {!signedIn && (
            <a href={signupUrl} className="self-start rounded-full bg-[hsl(var(--succeedo-orange))] text-white px-6 py-3 text-[15px] font-medium no-underline" data-testid="landing-signup">
              {result ? 'See your value range and plan: create your free account' : 'Create your free account'}
            </a>
          )}
          <p className="m-0 flex items-start gap-2 text-[12px] text-muted-foreground">
            <ShieldCheck size={14} className="shrink-0 mt-0.5" />
            {fromAdvisor
              ? `${advisor.name} sees that you joined and how far you've got. They see what you work on only when you choose to send them a brief, and never your conversations with your coach.`
              : `No advisor sees anything about you unless you ask to be contacted or choose to send a brief.`}
          </p>
          {!signedIn && (
            <p className="m-0 text-[13px] text-muted-foreground">
              Already on Succeedo? <a href={signupUrl}>Sign in</a>, then open this link again.
            </p>
          )}
        </section>
      </div>
    </main>
  );
}
